/**
 * FISHING — a catch-a-fish minigame in the garden pond. The player casts a bobber onto a water cell; after
 * a short randomised wait a fish BITES; they get a brief window to react, then the "reel it in" struggle
 * minigame (see FishingGame.tsx) decides the catch. This module owns the PURE data + logic: the fish
 * roster (stats + bespoke ART specs), bite timings, the struggle tuning, and the caught tally.
 */

import { coerceReveals } from "./clueReveal";
import type { ReleaseId } from "./releases";

/** How a fish is drawn — a compact spec the procedural <FishArt> renderer turns into a bespoke SVG. All
 *  distances are in a 100(long)×60(tall) body box; colours are hex strings. */
export interface FishArtSpec {
  /** overall body length : height as a fraction of the box (1 = fills it). Long slim fish → high w, low h. */
  bodyW: number;
  bodyH: number;
  /** belly fullness (0 = streamlined, 1 = round/fat) and how far back the widest point sits (0..1). */
  belly: number;
  bellyAt: number;
  /** tail style + how tall/forked it is. */
  tail: "fan" | "fork" | "round" | "long";
  tailH: number;
  /** dorsal fin: size + whether it's a tall sail. */
  dorsal: number;
  sail?: boolean;
  /** colours. */
  back: string;    // top/back colour
  bellyC: string;  // underside colour
  fin: string;     // fin membrane colour
  /** surface pattern. */
  pattern?: "stripes" | "spots" | "band" | "mottle" | "none";
  patternC?: string;
  /** an optional fine SPECKLE overlay (small dense spots) ON TOP of the base pattern — trout/salmon/pike
   *  spotting. The value is the speckle colour; density scales with the body. Combinable with `pattern`. */
  speckle?: string;
  /** subtle scale-shimmer overlay strength 0..1 (carp/tench read as scaled, not smooth). */
  scales?: number;
  /** feature flags for character. */
  barbels?: boolean;   // catfish whiskers
  bigEye?: boolean;
  glow?: string;       // a soft aura colour (rare/exotic fish)
  /** SUNFISH ear-flap spot: a small coloured dot on the rear edge of the gill cover (pumpkinseed's red-
   *  orange "ear", bluegill's dark flap). The value is the spot colour. */
  earSpot?: string;
  /** iridescent wavy CHEEK lines (pumpkinseed) — a few short curved lines on the operculum. The value is
   *  their colour. */
  cheekLines?: string;
  /** HEAD/forehead profile — "blunt" (rounded, carp/tench), "steep" (high forehead, deep panfish like
   *  crappie/bluegill), or "pointed" (tapered snout, streamlined). Default "blunt". Shapes the snout curve. */
  head?: "blunt" | "steep" | "pointed";
  /** draw an ANAL fin on the rear belly (most true fish have one) — adds realism + a lower silhouette. */
  analFin?: boolean;
  /** SECOND (soft) dorsal behind the main spiny dorsal — bass/perch read as two-part dorsals. */
  dorsal2?: boolean;
}

export interface FishDef {
  id: string;
  name: string;
  /** the content release this belongs to (sim/releases.ts) — hidden and never granted until that release is on.
   *  Absent (everything today) = always available. */
  release?: ReleaseId;
  icon: string;             // emoji fallback (banners/toasts)
  /** relative spawn weight (higher = more common). */
  weight: number;
  /** coins this fish sells for (future: a fish market). */
  value: number;
  /** how hard to land (0..1) — tunes the struggle (zone width, fish speed, gain/drain). */
  difficulty: number;
  /** on-screen size multiplier for the scene fish (small fry vs a big lunker). */
  sizeMul: number;
  /** where this fish lives: still "pond" water, flowing "stream" water, or "both". The water the
   *  player casts into is classified from its shape (see sim/waterType.ts). */
  habitat: WaterHabitat;
  art: FishArtSpec;
}

/** A fish's habitat, and the water types a cast can classify into. "both" fish bite anywhere. */
export type WaterHabitat = "pond" | "stream" | "both";

/**
 * SEVENTY bespoke freshwater fish — 35 for still POND water and 35 for flowing STREAM water. Each has a
 * distinct silhouette, palette and pattern so it reads as its own species in the scene + Field Guide.
 * Within each habitat the weights run common → rare (the little ones bite often; each habitat's prize
 * lunker is a thrill), and `value` tracks rarity so a rarer fish is worth more.
 *
 * THE WEIGHT LADDER — `weight` is the only rarity lever, and ONE function reads it: sim/fishSpawn.ts
 * `inferFishRarity` cuts it at absolute thresholds. That tier is what the spawn sim rolls on AND what the
 * Field Guide prints (via fishRarityOf → ui/rarityBadge), so there is no second derivation to keep in
 * step — a fish can no longer read "Legendary" in the guide while spawning at common rates. The
 * roster spans 4.25–100 and each habitat carries a full, identically shaped ladder (a cast is filtered to
 * its habitat, so a half-ladder would leave that water with no commons or no trophy):
 *
 *   Common     82–100    7 per habitat    Uncommon   62–79    9 per habitat
 *   Rare       38–58     8 per habitat    Very Rare  19–35    7 per habitat
 *   Legendary  4.25–16.5  4 per habitat
 *
 * WEIGHT IS A BUCKET, NOT A RATE. The shipped spawn model is DIRECT PERCENTAGES: `baseFishChanceOf` looks
 * the fish's TIER up in `FISH_CONFIG.tierBasePct` (common 40% → legendary 2%), conditions and drivers move
 * that number, and `pickFishByChance` rolls the results against each other. Two fish in the same band have
 * the SAME base chance no matter how far apart their weights sit, so the exact value only matters for which
 * side of a threshold it lands on. There is no weighted roll in the game any more: `pickFish` below is
 * weight-proportional but is called only by the dev screenshot harness.
 *
 * So the spacing inside a band is ordering and headroom, not tuning — moving a fish from 16.5 to 5 changes
 * nothing a player can observe unless it crosses a seam. BOTTOM-HEAVY BY COUNT is still the point: commons
 * and uncommons are 16 of every 35, and the 4 legendaries per habitat each sit at a 2% base. The previous
 * ladder ran 2.5–42 and put 26 of 70 fish in Legendary, which is why a "legendary" stopped reading as an
 * event — the fix was the COUNT in that bucket, not the weights themselves.
 *
 * Adding a fish: place it inside a band with a little clearance from the seam (rounding must not push it
 * across), and keep the two habitats' counts equal. The bands have deliberate GAPS between them — nothing
 * should sit at 80 or 59 or 36–37 or 17–18.
 *
 * The water the player casts into is classified from its SHAPE (see sim/waterType.ts) → pickFish filters
 * the roster to that habitat. Design language:
 *   POND (still)   → deeper, rounder bodies (higher bodyH + belly), fan/round tails, sails — panfish,
 *                    carp, catfish, bullheads; warmer / mottled / spotted palettes.
 *   STREAM (flow)  → slimmer, streamlined bodies (high bodyW, low bodyH, low belly), forked/long tails —
 *                    trout, dace, salmon, darters; cooler / silvery / speckled palettes.
 */
export const FISH: FishDef[] = [
  // ───────────────────────────── REAL POND (18) ─────────────────────────────
  // sizeMul is calibrated to real relative length (a minnow is a fraction of a koi): tiny panfish ~0.5,
  // mid pond fish ~0.9–1.2, big lunkers 1.6–1.9. The viewer + scene render at this scale.
  {
    // PUMPKINSEED — a deep round disc panfish: olive-green back, warm orange belly, iridescent blue-green
    // wavy cheek lines, orange flank flecks, and the signature red-orange "ear" spot on the gill flap.
    id: "sunfish", name: "Pumpkinseed", icon: "🐠", weight: 100, value: 10, difficulty: 0.2, sizeMul: 0.52,
    habitat: "pond",
    art: { bodyW: 0.62, bodyH: 0.72, belly: 0.9, bellyAt: 0.5, tail: "fan", tailH: 0.56, dorsal: 0.78, sail: true,
      back: "#5a8f3e", bellyC: "#ffbf3a", fin: "#e8842e", pattern: "spots", patternC: "#f26a2e",
      scales: 0.5, earSpot: "#e23b2e", cheekLines: "#4fb7c4" },
  },
  {
    // BLUEGILL — a deep, round panfish: blue-green flank with faint vertical bars, a coppery-orange breast,
    // a steep forehead, a small upturned mouth, and the dark "ear" flap sunfish carry.
    id: "bluegill", name: "Bluegill", icon: "🐠", weight: 97, value: 14, difficulty: 0.26, sizeMul: 0.6,
    habitat: "pond",
    art: { bodyW: 0.6, bodyH: 0.74, belly: 0.86, bellyAt: 0.52, tail: "fan", tailH: 0.54, dorsal: 0.8, sail: true,
      back: "#2e6f74", bellyC: "#e39a52", fin: "#264a6e", pattern: "stripes", patternC: "#173a4a",
      head: "steep", analFin: true, earSpot: "#1c2733", cheekLines: "#3fa0c0", scales: 0.4 },
  },
  {
    // GOLDEN SHINER — the pond's common tiddler: a deep, laterally-flattened little cyprinid with a gold
    // flank fading to silver over a KEELED belly, a fine dark decurved lateral line, a small upturned mouth,
    // a far-back dorsal and a long falcate anal fin.
    id: "goldenShiner", name: "Golden Shiner", icon: "🐟", weight: 93, value: 18, difficulty: 0.33, sizeMul: 0.46,
    habitat: "pond",
    art: { bodyW: 0.86, bodyH: 0.52, belly: 0.78, bellyAt: 0.54, tail: "fork", tailH: 0.62, dorsal: 0.44,
      back: "#9c7522", bellyC: "#f5f2e8", fin: "#b0812f", pattern: "band", patternC: "#6f4a12",
      head: "pointed", analFin: true, scales: 0.45 },
  },
  {
    // CENTRAL MUDMINNOW — a stubby little tiny-water fish: a short, thick, blunt-nosed body with barely any
    // taper, a dark olive flank crossed by faint vertical bars, a dorsal set far back over the anal, and a
    // broad ROUNDED paddle of a tail with a black bar at its root.
    id: "mudminnow", name: "Central Mudminnow", icon: "🪸", weight: 87, value: 22, difficulty: 0.36, sizeMul: 0.42,
    habitat: "pond",
    art: { bodyW: 0.72, bodyH: 0.58, belly: 0.88, bellyAt: 0.46, tail: "round", tailH: 0.58, dorsal: 0.40,
      back: "#5d5233", bellyC: "#d8cba6", fin: "#4a4128", pattern: "stripes", patternC: "#2c2614",
      head: "blunt", analFin: true, scales: 0.3 },
  },
  {
    // SPECKLED CRAPPIE — a deep, laterally-flattened silvery panfish covered in dark green mottled speckles,
    // a steep head, a big sail dorsal, and a fine anal fin.
    id: "crappie", name: "Speckled Crappie", icon: "🐠", weight: 84, value: 24, difficulty: 0.4, sizeMul: 0.78,
    habitat: "pond",
    art: { bodyW: 0.72, bodyH: 0.66, belly: 0.72, bellyAt: 0.48, tail: "fan", tailH: 0.6, dorsal: 0.82, sail: true,
      back: "#7a8d80", bellyC: "#eef2ec", fin: "#93a49a", pattern: "mottle", patternC: "#2a3a2e",
      head: "steep", analFin: true, scales: 0.35, speckle: "#33452f" },
  },
  {
    // COMMON BREAM — the SLAB: the roster's only extremely deep, laterally flattened fish. A bronze-grey
    // diamond at 85.7 pct depth over length (against the Mirror Carp's 44.3), a tiny head with a small
    // DOWN-TURNED protrusible mouth, a long low anal fin under most of the underside, a short high dorsal
    // set back, and a deeply forked tail. Deliberately NO bars and NO spiny dorsal — it is not a percid.
    // The id stays `europeanPerch` because five files reference it; only the fish changed.
    id: "europeanPerch", name: "Common Bream", icon: "🐟", weight: 79, value: 33, difficulty: 0.4, sizeMul: 0.66,
    habitat: "pond",
    art: { bodyW: 0.72, bodyH: 1.0, belly: 0.96, bellyAt: 0.5, tail: "fork", tailH: 0.62, dorsal: 0.7,
      back: "#6b5a2e", bellyC: "#d8c99e", fin: "#4a4f4a", pattern: "none",
      head: "steep", analFin: true, scales: 0.55 },
  },
  {
    // GOLDEN TENCH — a stocky, deep olive-gold fish with tiny embedded scales (a slimy sheen), a blunt head,
    // a down-turned barbelled "sucker" mouth, a thick rounded tail, and small rounded fins.
    id: "goldenTench", name: "Golden Tench", icon: "🐟", weight: 74, value: 34, difficulty: 0.46, sizeMul: 0.98,
    habitat: "pond",
    art: { bodyW: 0.82, bodyH: 0.62, belly: 0.82, bellyAt: 0.5, tail: "round", tailH: 0.5, dorsal: 0.44,
      back: "#c99328", bellyC: "#ffe79a", fin: "#8a6420", pattern: "none", scales: 0.95,
      head: "blunt", barbels: true, analFin: true, bigEye: true },
  },
  {
    // RUDD — the Ruby Roach's confusion twin, drawn to be told apart from it: a deep brassy-gold flank over a
    // warm cream belly (not the roach's silver), BLOOD-red fins rather than the roach's red-orange, and a
    // markedly UPTURNED mouth — the snout tip rides above the midline on a jutting lower jaw.
    id: "rudd", name: "Rudd", icon: "🐟", weight: 70, value: 41, difficulty: 0.53, sizeMul: 0.74,
    habitat: "pond",
    art: { bodyW: 0.78, bodyH: 0.62, belly: 0.82, bellyAt: 0.52, tail: "fork", tailH: 0.58, dorsal: 0.52,
      back: "#6f5626", bellyC: "#f6e6b4", fin: "#a8121a", pattern: "band", patternC: "#8a6a20",
      head: "pointed", analFin: true, scales: 0.45 },
  },
  {
    // LARGEMOUTH BASS — an elongated predator: a big WIDE gape reaching back under the eye (its namesake),
    // a bold dark lateral band, a two-part (spiny + soft) dorsal, an anal fin, and a forked tail.
    id: "bass", name: "Largemouth Bass", icon: "🐟", weight: 66, value: 52, difficulty: 0.6, sizeMul: 1.24,
    habitat: "pond",
    art: { bodyW: 0.94, bodyH: 0.5, belly: 0.6, bellyAt: 0.42, tail: "fork", tailH: 0.54, dorsal: 0.6, sail: true,
      back: "#4f7d4a", bellyC: "#eef0d6", fin: "#4a6a44", pattern: "band", patternC: "#1a2c18",
      head: "pointed", dorsal2: true, analFin: true, bigEye: true, scales: 0.3 },
  },
  {
    // MIRROR CARP — a deep, heavy-bodied golden carp scattered with big irregular "mirror" scale plates, a
    // blunt head, a down-turned barbelled sucker mouth, a long dorsal, and an anal fin.
    id: "carp", name: "Mirror Carp", icon: "🐟", weight: 62, value: 66, difficulty: 0.64, sizeMul: 1.55,
    habitat: "pond",
    art: { bodyW: 0.86, bodyH: 0.66, belly: 0.86, bellyAt: 0.5, tail: "fork", tailH: 0.5, dorsal: 0.66,
      back: "#9a8442", bellyC: "#f2e4a8", fin: "#6a5a2c", pattern: "spots", patternC: "#5a4a24", scales: 0.6,
      head: "blunt", barbels: true, analFin: true, bigEye: true },
  },
  {
    // CHAIN PICKEREL — the chain-link fish: a dark RETICULATED NET over a bright yellow-green flank, on the
    // long duck-billed snout of a small pike. Told from the Northern Pike by the pattern's TOPOLOGY (one
    // connected net vs 43 separate pale beans) and its POLARITY (dark on pale vs pale on dark), plus a
    // shorter bill, a stockier body and the dark cheek teardrop under the eye that a pike does not carry.
    id: "chainPickerel", name: "Chain Pickerel", icon: "🐟", weight: 55, value: 73, difficulty: 0.72, sizeMul: 1.18,
    habitat: "pond",
    art: { bodyW: 0.96, bodyH: 0.34, belly: 0.36, bellyAt: 0.58, tail: "fork", tailH: 0.48, dorsal: 0.34,
      back: "#5f8f2a", bellyC: "#f2f0d4", fin: "#7e9a4a", pattern: "none", patternC: "#16240f" },
  },
  {
    id: "bullhead", name: "Brown Bullhead", icon: "🐡", weight: 52, value: 84, difficulty: 0.72, sizeMul: 1.05,
    habitat: "pond",
    art: { bodyW: 0.94, bodyH: 0.42, belly: 0.74, bellyAt: 0.34, tail: "round", tailH: 0.44, dorsal: 0.36,
      back: "#493c30", bellyC: "#d8c8a8", fin: "#5a4d40", pattern: "mottle", patternC: "#2a221b", barbels: true },
  },
  {
    id: "catfish", name: "Whiskered Catfish", icon: "🐡", weight: 43, value: 100, difficulty: 0.76, sizeMul: 1.7,
    habitat: "pond",
    art: { bodyW: 0.98, bodyH: 0.38, belly: 0.72, bellyAt: 0.32, tail: "round", tailH: 0.42, dorsal: 0.38,
      back: "#524b43", bellyC: "#e4d9c2", fin: "#685f54", pattern: "mottle", patternC: "#322d27", barbels: true, bigEye: true },
  },
  {
    // BOWFIN — the living fossil: one enormous UNDULATING DORSAL running 64 pct of the back, a heavy rounded
    // paddle tail, an armoured bony head (cheek plates + the archaic gular shield under the throat, and no
    // barbels at all), and the black ORANGE-RINGED EYESPOT at the tail root. Told from the Brown Bullhead by
    // that dorsal (4.7x the bullhead's), armour instead of whiskers, and the eyespot the bullhead lacks.
    id: "bowfin", name: "Bowfin", icon: "🐡", weight: 32, value: 127, difficulty: 0.79, sizeMul: 1.36,
    habitat: "pond",
    art: { bodyW: 0.96, bodyH: 0.44, belly: 0.76, bellyAt: 0.42, tail: "round", tailH: 0.46, dorsal: 0.4,
      back: "#5c6440", bellyC: "#e0d8ae", fin: "#4e5a38", pattern: "mottle", patternC: "#2b3320", bigEye: true },
  },
  {
    id: "eel", name: "Marsh Eel", icon: "🐍", weight: 23, value: 150, difficulty: 0.84, sizeMul: 1.45,
    habitat: "pond",
    art: { bodyW: 1.0, bodyH: 0.24, belly: 0.32, bellyAt: 0.5, tail: "long", tailH: 0.34, dorsal: 0.26,
      back: "#37452f", bellyC: "#cfc890", fin: "#4c5738", pattern: "mottle", patternC: "#1f2719" },
  },
  {
    // LONGNOSE GAR — the living arrow: a BEAK 42 pct of the body long, lined with interlocking teeth, on a
    // rigid armoured cylinder plated in diamond GANOID scales, with dorsal and anal opposed and jammed
    // against the tail like an arrow's flights. Told from the Northern Pike and the Chain Pickerel by that
    // beak (a thinning needle at 42 pct against their 23 and 19), a level back with no crown, and the
    // rhombic lattice where they wear beans and a chain.
    id: "longnoseGar", name: "Longnose Gar", icon: "🐊", weight: 16.5, value: 228, difficulty: 0.87, sizeMul: 1.8,
    habitat: "pond",
    art: { bodyW: 1.0, bodyH: 0.26, belly: 0.4, bellyAt: 0.5, tail: "fan", tailH: 0.44, dorsal: 0.3,
      back: "#6b7052", bellyC: "#efe6c8", fin: "#59613f", pattern: "mottle", patternC: "#23281a", scales: 0.6 },
  },
  {
    id: "koi", name: "Golden Koi", icon: "🎏", weight: 10.5, value: 260, difficulty: 0.9, sizeMul: 1.62,
    habitat: "pond",
    // NOTE: geometry levers here are inert for the koi (it's a bespoke render/fish/koi.tsx); the bespoke
    // renderer reads only the COLOURS. Base body is the roster's PALEST so the red/orange kohaku PATCHES
    // read against it — but pale means IVORY, not white. It used to be #f4f1ea into #fffdf7, which is
    // effectively pure white and made this the single brightest asset in the game (measured 0.416 mean,
    // 0.956 p98 — the roster's next brightest was 0.33). STYLE_GUIDE §2.2 caps the brightest thing in any
    // asset at ivory #f0ede0 and forbids white outright. The belly now sits exactly on that ceiling and the
    // back a step under it, so the back→belly direction survives; the patch red came down with it so it
    // stays a burnt red rather than a signal orange against the cooler body.
    art: { bodyW: 0.9, bodyH: 0.52, belly: 0.68, bellyAt: 0.48, tail: "long", tailH: 0.72, dorsal: 0.46,
      back: "#e4dfcd", bellyC: "#f0ede0", fin: "#f0d8b4", pattern: "mottle", patternC: "#c2381a", scales: 0.5,
      glow: "#ffcf7a", barbels: true, bigEye: true },
  },
  {
    // LAKE STURGEON — the top pond prize and the biggest fish in the game: an armoured wedge wearing three
    // rows of countable bony SCUTES, a raked shark tail whose upper lobe is 2.2x the lower, a bare shovel
    // snout, and — the diagnostic pair — an UNDERSLUNG sucker mouth set well back under the head with FOUR
    // barbels in a row ahead of it. Told from the barbelled catfish by its hard plated outline and that
    // projecting snout; from the Longnose Gar by sparse plates over bare hide vs a continuous lattice.
    // Slate-olive over cream, cooler than the gar's warm khaki so the two armoured pond fish never blend.
    id: "lakeSturgeon", name: "Lake Sturgeon", icon: "🐡", weight: 4.25, value: 298, difficulty: 0.92, sizeMul: 2.2,
    habitat: "pond",
    art: { bodyW: 1.0, bodyH: 0.3, belly: 0.5, bellyAt: 0.5, tail: "fan", tailH: 0.62, dorsal: 0.34,
      back: "#5a6558", bellyC: "#e8e2cc", fin: "#7d8878", pattern: "mottle", patternC: "#2c332b",
      barbels: true },
  },

  // ──────────────────────────── REAL STREAM (17) ────────────────────────────
  {
    id: "minnow", name: "Silver Minnow", icon: "🐟", weight: 100, value: 8, difficulty: 0.12, sizeMul: 0.4,
    habitat: "stream",
    art: { bodyW: 0.92, bodyH: 0.32, belly: 0.28, bellyAt: 0.42, tail: "fork", tailH: 0.5, dorsal: 0.36,
      back: "#8ea3b3", bellyC: "#f2f8fc", fin: "#c2ced8", pattern: "band", patternC: "#b6c6d2" },
  },
  {
    id: "dace", name: "Common Dace", icon: "🐟", weight: 97, value: 14, difficulty: 0.22, sizeMul: 0.5,
    habitat: "stream",
    art: { bodyW: 0.94, bodyH: 0.34, belly: 0.32, bellyAt: 0.44, tail: "fork", tailH: 0.54, dorsal: 0.4,
      back: "#77869a", bellyC: "#f6fafc", fin: "#c0ccd6", pattern: "band", patternC: "#9aa8b6" },
  },
  {
    id: "roach", name: "Ruby Roach", icon: "🐟", weight: 90, value: 22, difficulty: 0.34, sizeMul: 0.62,
    habitat: "stream",
    art: { bodyW: 0.82, bodyH: 0.46, belly: 0.52, bellyAt: 0.46, tail: "fork", tailH: 0.58, dorsal: 0.54,
      back: "#586878", bellyC: "#f2f5f8", fin: "#d6483f", pattern: "band", patternC: "#8fa0ad" },
  },
  {
    id: "darter", name: "Rainbow Darter", icon: "🐟", weight: 82, value: 30, difficulty: 0.4, sizeMul: 0.44,
    habitat: "stream",
    art: { bodyW: 0.88, bodyH: 0.4, belly: 0.44, bellyAt: 0.5, tail: "fan", tailH: 0.46, dorsal: 0.62, sail: true,
      back: "#356aa0", bellyC: "#ffcf72", fin: "#e2683f", pattern: "stripes", patternC: "#20365e" },
  },
  {
    // GUDGEON — a small bottom-hugger, and the shape is the argument: it has a FLAT BELLY, running dead
    // level for 48 pct of the body, because it rests on gravel rather than swimming over it. Every other
    // small fish on the shelf is a spindle. Under the blunt underslung snout hangs ONE barbel pair at the
    // mouth corners — two, where the Bullhead has eight — drawn as tapering shapes hanging DOWN and forward
    // toward the substrate, since a barbel swept back along the flank is an organ that cannot feel gravel.
    // Along the lateral line runs the field mark: 8 DISCRETE dark blotches at a third coverage, a countable
    // series rather than the Largemouth Bass's torn stripe, each verified to sit wholly on the flank. Low
    // wide pectorals held out sideways as outriggers. Told from the Rainbow Darter, the other small stream
    // bottom fish, by posture — that one PROPS nose-up on planted fan pectorals, this one LIES level — and
    // by pattern: a horizontal row of round spots against a vertical curtain of blue-and-orange bars.
    // Deliberately the drabbest small fish in the stream: a gudgeon on gravel is not trying to be seen.
    // See gudgeon.tsx.
    id: "gudgeon", name: "Gudgeon", icon: "🐟", weight: 74, value: 36, difficulty: 0.46, sizeMul: 0.42,
    habitat: "stream",
    art: { bodyW: 0.9, bodyH: 0.38, belly: 0.5, bellyAt: 0.62, tail: "fork", tailH: 0.46, dorsal: 0.5,
      back: "#8a7a54", bellyC: "#f0ead8", fin: "#a89570", pattern: "spots", patternC: "#413524" },
  },
  {
    // STONE LOACH — the stream's first NOCTURNAL real fish, and the one whose markings are SUBSTRATE
    // PATTERNING rather than decoration: saddles, marbling and gravel grain in three scales, all built in
    // the body's own parameter space and all clipped to the silhouette, so the flank reads as wet gravel
    // with no clean ground anywhere. An earlier version drew each blotch twice so its outboard half landed
    // past the outline; on a sprite that read as stones stuck to the fish, and nothing crosses the edge
    // now. On an eel-like body: 6.4:1, near-cylindrical, a rod that slides under a stone rather than a
    // spindle. Six barbels in three fanned pairs around a downturned mouth — the name Barbatula is about
    // the count. Told from the Gudgeon, the other small barbelled bottom fish, by almost everything: rod
    // against wedge, six barbels against two, an uncountable marbled field against 8 countable spots on
    // the lateral line, and night against day. See stoneLoach.tsx.
    id: "stoneLoach", name: "Stone Loach", icon: "🐟", weight: 70, value: 41, difficulty: 0.48, sizeMul: 0.44,
    habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.24, belly: 0.4, bellyAt: 0.5, tail: "round", tailH: 0.4, dorsal: 0.34,
      back: "#6b5a3e", bellyC: "#c9b78e", fin: "#8a765a", pattern: "mottle", patternC: "#33291b" },
  },
  {
    id: "trout", name: "Rainbow Trout", icon: "🐟", weight: 68, value: 46, difficulty: 0.52, sizeMul: 1.05,
    habitat: "stream",
    art: { bodyW: 0.94, bodyH: 0.4, belly: 0.42, bellyAt: 0.42, tail: "fork", tailH: 0.5, dorsal: 0.46,
      back: "#5a7482", bellyC: "#f4eef0", fin: "#a88494", pattern: "band", patternC: "#d06a86", speckle: "#26323e" },
  },
  {
    id: "brookTrout", name: "Brook Trout", icon: "🐟", weight: 62, value: 58, difficulty: 0.56, sizeMul: 0.92,
    habitat: "stream",
    art: { bodyW: 0.92, bodyH: 0.42, belly: 0.46, bellyAt: 0.44, tail: "fork", tailH: 0.48, dorsal: 0.44,
      back: "#3e5646", bellyC: "#eaa85e", fin: "#c05a2a", pattern: "spots", patternC: "#f0c85a", speckle: "#e0583a" },
  },
  {
    // BLEAK — the roster's surface fish, built around the CEILING: it hangs an inch under the film taking
    // flies off it, so the lower jaw is the forwardmost point of the animal, the head closes as an
    // upward-facing wedge with the gape cut steeply into it, and the eye rides high to watch the surface.
    // The flank is not painted: it is ONE guanine-platelet resonance evaluated at six viewing angles, and
    // because a quarter-wave stack blue-shifts as it turns away, the same reflector gives a dusky blue-green
    // back and a near-white belly out of a single number. Deliberately no specular band anywhere — the whole
    // side is the mirror. Very slender, with the LONG anal fin that is the real bleak's diagnostic (20.5 pct
    // of the body against the Dace's 9.5). Told from the Common Dace, the other silver stream cyprinid, by
    // opposite mass distribution — that one is deepest well forward, this one behind centre — and by device:
    // the Dace's is a crisp near-black lateral line, this one's is the jaw, so its own line is deliberately
    // the faint one. See bleak.tsx.
    id: "bleak", name: "Bleak", icon: "🐟", weight: 58, value: 63, difficulty: 0.6, sizeMul: 0.54,
    habitat: "stream",
    art: { bodyW: 0.96, bodyH: 0.28, belly: 0.34, bellyAt: 0.54, tail: "fork", tailH: 0.54, dorsal: 0.38,
      back: "#197765", bellyC: "#f6f8fe", fin: "#8fa8ab", pattern: "none" },
  },
  {
    id: "grayling", name: "Sail Grayling", icon: "🐟", weight: 49, value: 74, difficulty: 0.64, sizeMul: 0.98,
    habitat: "stream",
    art: { bodyW: 0.92, bodyH: 0.4, belly: 0.4, bellyAt: 0.44, tail: "fork", tailH: 0.5, dorsal: 1.0, sail: true,
      back: "#6b7088", bellyC: "#eef0f6", fin: "#8f66a8", pattern: "spots", patternC: "#353850" },
  },
  {
    // BARBEL — the strongest fish in the stream for its size, and the art is about where that strength
    // LIVES. The whole body is derived from one girth curve peaking at the shoulder and falling to a wrist
    // a FIFTH as deep at the caudal root: a 5:1 taper, where the difference between the two is what reads
    // as power rather than the size of either. Its shading is girth-relative — the tonal bands pinch as the
    // section shrinks, the way tone does on a real tapering cylinder — so it reads as a round mass of
    // muscle instead of a flat bronze cut-out, which is what the roster's ordinary vertical ramp gives.
    // A long conical snout OVERHANGS an underslung mouth, with four short thick barbels (two on the snout,
    // two at the corners) as stiff sensory pegs, not the catfish's trailing whiskers. Big orange-TINTED
    // fins — the flank's own bronze pushed toward orange, never flat orange — and the deepest forked tail
    // on the real stream shelf. Told from the River Chub, the other big bronze cyprinid: that one is
    // accountable to a BLUNT head and a deliberately matte brass plateau; this is a spike and a cylinder.
    // The roster's first dawn fish. See barbel.tsx.
    id: "barbel", name: "Barbel", icon: "🐟", weight: 43, value: 83, difficulty: 0.66, sizeMul: 1.15,
    habitat: "stream",
    art: { bodyW: 0.96, bodyH: 0.44, belly: 0.5, bellyAt: 0.42, tail: "fork", tailH: 0.62, dorsal: 0.56,
      back: "#7a6234", bellyC: "#efe2c2", fin: "#b98a4a", pattern: "none", scales: 0.35 },
  },
  {
    id: "chub", name: "River Chub", icon: "🐟", weight: 38, value: 90, difficulty: 0.68, sizeMul: 0.82,
    habitat: "stream",
    art: { bodyW: 0.94, bodyH: 0.46, belly: 0.56, bellyAt: 0.44, tail: "fork", tailH: 0.52, dorsal: 0.46,
      back: "#7a7d5c", bellyC: "#ece9cf", fin: "#bd6440", pattern: "none", scales: 0.85, bigEye: true },
  },
  {
    // ZANDER — a deep-water night hunter, and the sprite is arranged around ONE feature: an eye that shines
    // back at you. Its retina carries a tapetum lucidum, so unabsorbed light is sent back out the way it
    // came — which INVERTS the eye's whole value structure. Every other eye on the roster is a coloured
    // iris around a dark absorbing pupil; here the pupil is the BRIGHTEST thing on the animal, a hot core
    // running out through tapetal blue-green, with a dark iris to be bright against, a bloom outside the
    // eyeball, and deliberately no specular catchlight (a catchlight says "lit object" where a tapetum says
    // "source"). Over a pale olive flank with FAINT bars — near-parallel-sided and a fraction of the
    // European Perch's contrast, since that fish's file is accountable to bold near-black wedges and two
    // barred percids differing only in strength would be a weak separation. Two dorsals with real bare back
    // between them, the first tall and spiny. Told from the Northern Pike by a conical percid head against
    // that fish's 22u duck bill, two dorsals against one set far back, and faint dark bars on pale against
    // 43 pale bean spots on dark — the polarity inverted as well as the shape. See zander.tsx.
    id: "zander", name: "Zander", icon: "🐟", weight: 32, value: 121, difficulty: 0.75, sizeMul: 1.42,
    habitat: "stream",
    art: { bodyW: 0.98, bodyH: 0.34, belly: 0.4, bellyAt: 0.46, tail: "fork", tailH: 0.54, dorsal: 0.72, sail: true,
      back: "#8f8a5c", bellyC: "#f2eed9", fin: "#a39a72", pattern: "stripes", patternC: "#4a4630" },
  },
  {
    id: "pike", name: "Northern Pike", icon: "🐟", weight: 25.5, value: 130, difficulty: 0.82, sizeMul: 1.6,
    habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.32, belly: 0.32, bellyAt: 0.62, tail: "fork", tailH: 0.48, dorsal: 0.34,
      back: "#4a6b3c", bellyC: "#eef2df", fin: "#84995f", pattern: "spots", patternC: "#d2e0a4" },
  },
  {
    // BURBOT — the only freshwater cod, and the one fish on the roster whose pattern is a CONNECTED NETWORK
    // rather than a set of separate marks. Every other patterned fish here distributes discrete blobs over a
    // ground (the Bowfin's chevrons, the Gudgeon's countable spots, the Pike's beans); marbling is a
    // different kind of thing, so the pale channels are walked across the flank STEERED BY A NOISE FIELD and
    // fuse where they cross, which is what makes it reticulated instead of a scatter of worms. Pale veins on
    // a dark ground, in that order — a burbot is dark olive veined with cream, not the reverse. ONE chin
    // barbel, on the midline: the extreme of the roster's barbel-count series (loach six, barbel four,
    // gudgeon two). A short first dorsal then a long low second running aft, with a matching long anal —
    // eel-like at a glance, and told from the Marsh Eel by the thing that matters: those fins STOP at the
    // peduncle and a separate rounded tail sits behind them, where the eel's file is accountable to one
    // continuous median fin wrapping the tail tip. It also runs the standard swim, not the eel's serpentine
    // customAnim — elongate is a shape here, not a motion. See burbot.tsx.
    id: "burbot", name: "Burbot", icon: "🐡", weight: 21, value: 219, difficulty: 0.86, sizeMul: 1.22,
    habitat: "stream",
    art: { bodyW: 0.98, bodyH: 0.3, belly: 0.42, bellyAt: 0.5, tail: "round", tailH: 0.42, dorsal: 0.34,
      back: "#5c5a30", bellyC: "#ddd2a4", fin: "#7a7444", pattern: "mottle", patternC: "#c9be8a" },
  },
  {
    // HUCHEN — the Danube salmon and the top river prize, gated on a long stream. It is the hardest
    // distinctness problem on the shelf: a large speckled salmonid with an adipose fin standing next to the
    // Silver Salmon, which is also one. Three things separate them, none alone sufficient. COLOUR: that
    // fish is chrome (a five-stop ramp with two lightness reversals); this one is matte bronze carrying a
    // broad COPPER FLUSH along the mid-flank, built with a real vertical falloff so it blooms rather than
    // striping. MASS: the salmon is accountable to 4.00:1 fineness with a 22 pct wrist — a lean coho; a
    // huchen eats other fish whole and runs nearer 3.4:1 on a much heavier peduncle. And SPECKLING: the
    // salmon's is 40 discrete blobs that stop behind the gill, where this is a fine dense peppering that
    // runs forward over the operculum and skull — spots on the face are the fastest tell in the hand.
    // NOTE: weight 16.5 is the top of the stream LEGENDARY band (see the weight ladder above) — the
    // first fish past the Very Rare seam at 19, not a mid-tier fish. Its distance from the rest of the
    // band buys nothing at the table: every legendary shares one 2% base, so only the SEAM matters —
    // raise this above 19 and the top river prize drops to Very Rare. See huchen.tsx.
    id: "huchen", name: "Huchen", icon: "🐟", weight: 16.5, value: 242, difficulty: 0.88, sizeMul: 1.95,
    habitat: "stream",
    art: { bodyW: 0.96, bodyH: 0.46, belly: 0.5, bellyAt: 0.46, tail: "fork", tailH: 0.6, dorsal: 0.5,
      back: "#4e5a44", bellyC: "#ece4d2", fin: "#8a7a5e", pattern: "none", speckle: "#241a12" },
  },
  {
    id: "salmon", name: "Silver Salmon", icon: "🐟", weight: 6.75, value: 280, difficulty: 0.9, sizeMul: 1.85,
    habitat: "stream",
    art: { bodyW: 0.98, bodyH: 0.4, belly: 0.44, bellyAt: 0.42, tail: "fork", tailH: 0.54, dorsal: 0.44,
      back: "#8494a8", bellyC: "#fcf6f1", fin: "#b0a4c0", pattern: "none", speckle: "#2a3648",
      glow: "#cfe0f0", bigEye: true },
  },

  // ══════════════ FANTASY WAVE — 10 POND + 10 STREAM (bespoke; geometry levers unused, colours drive them) ══════════════
  // ── FANTASY POND (17) ──
  {
    id: "dewdrop", name: "Dewdrop", icon: "💧", weight: 90, value: 18, difficulty: 0.24, sizeMul: 0.5, habitat: "pond",
    art: { bodyW: 0.5, bodyH: 0.72, belly: 0.9, bellyAt: 0.5, tail: "round", tailH: 0.5, dorsal: 0.4,
      back: "#a8dced", bellyC: "#eafaff", fin: "#bfe8f5", pattern: "none", glow: "#a8ecff" },
  },
  {
    id: "lilypadLurker", name: "Lilypad Lurker", icon: "🪷", weight: 82, value: 26, difficulty: 0.36, sizeMul: 0.95, habitat: "pond",
    art: { bodyW: 0.7, bodyH: 0.72, belly: 0.82, bellyAt: 0.5, tail: "fan", tailH: 0.54, dorsal: 0.5,
      back: "#4f8f52", bellyC: "#c8e0a4", fin: "#3f7040", pattern: "none", patternC: "#2e5a34", glow: "#7fdca0" },
  },
  {
    // ROOTLING — a fish that hides as a sunken root. Where the Lilypad Lurker mimics a smooth thing (a pad,
    // with one wedge cut out of it), this one mimics a KNOTTED one: a lumpy burled log with branch stumps
    // for fins, bark fissures running with the grain, three knots, and two leaves still snagged on it. Wood
    // browns and a sour green — the drabbest fish in the pond, which is the point. See rootling.tsx.
    id: "rootling", name: "Rootling", icon: "🪵", weight: 76, value: 32, difficulty: 0.42, sizeMul: 0.72,
    habitat: "pond",
    art: { bodyW: 0.78, bodyH: 0.56, belly: 0.7, bellyAt: 0.5, tail: "round", tailH: 0.44, dorsal: 0.36,
      back: "#6b5334", bellyC: "#3f3220", fin: "#54402a", pattern: "none", patternC: "#3a2c1e", glow: "#8fae6a" },
  },
  {
    id: "moonscale", name: "Moonscale", icon: "🌙", weight: 72, value: 40, difficulty: 0.44, sizeMul: 1.0, habitat: "pond",
    art: { bodyW: 0.66, bodyH: 0.74, belly: 0.86, bellyAt: 0.5, tail: "fan", tailH: 0.58, dorsal: 0.6, sail: true,
      back: "#7a8fb8", bellyC: "#eef4ff", fin: "#c8d6ee", pattern: "none", scales: 0.5, glow: "#bcd6ff" },
  },
  {
    id: "thornback", name: "Thornback", icon: "🐡", weight: 68, value: 46, difficulty: 0.52, sizeMul: 1.08, habitat: "pond",
    art: { bodyW: 0.7, bodyH: 0.7, belly: 0.8, bellyAt: 0.5, tail: "fork", tailH: 0.5, dorsal: 0.5,
      back: "#6f8f4a", bellyC: "#d2dca0", fin: "#4f6a30", pattern: "none", patternC: "#2e4a1e", glow: "#9ad06a" },
  },
  {
    id: "emberfin", name: "Emberfin", icon: "🔥", weight: 64, value: 64, difficulty: 0.6, sizeMul: 1.16, habitat: "pond",
    art: { bodyW: 0.68, bodyH: 0.7, belly: 0.78, bellyAt: 0.5, tail: "fan", tailH: 0.56, dorsal: 0.56,
      back: "#241a18", bellyC: "#5a2e1e", fin: "#3a2018", pattern: "none", glow: "#ff6a1e" },
  },
  {
    id: "sporecarp", name: "Sporecarp", icon: "🍄", weight: 58, value: 78, difficulty: 0.62, sizeMul: 1.3, habitat: "pond",
    art: { bodyW: 0.74, bodyH: 0.72, belly: 0.86, bellyAt: 0.5, tail: "round", tailH: 0.5, dorsal: 0.48,
      back: "#6a5a78", bellyC: "#cabcd8", fin: "#4e4058", pattern: "none", glow: "#c89aff" },
  },
  {
    // CLOUDCARP — a CARP-SHAPED HOLE IN THE WATER. It does not wear the sky as a pattern; the surface is gone
    // over its outline and you are looking through it at the sky beyond, so the fish has no shading of its
    // own: no back-to-belly ramp, no scales, no lateral line, no rim, no glow, and an eye and mouth drawn as
    // voids rather than as features. The sky is a real Rayleigh integral (it whitens toward the horizon by
    // LOSING chroma, which a blue-to-white lerp cannot do). The weather is a CONTINUOUS vapour surface whose
    // banks are derived from the body's own contour, not discrete clouds — closed clouds lying on a flank
    // read as stickers, which is what this used to be. It drifts in WORLD space, so it holds still while the
    // fish swims across it; a painted sky would ride the animal, which is the giveaway this exists to avoid.
    // The SILHOUETTE billows too, so the outline carries the cloud idea rather than the decoration having to.
    // Read as a carp from the cut edge alone: arched back, long cyprinid dorsal, barbels.
    // Told from the Mirrorpike, which also shows a scene, by opposite optics — that one is a mirror shaded by
    // its own curvature with the whole sky crushed into one narrow Snell band on a lean stream pike; this one
    // is a window with nothing but sky in it. See cloudcarp.tsx.
    id: "cloudcarp", name: "Cloudcarp", icon: "☁️", weight: 49, value: 85, difficulty: 0.66, sizeMul: 1.28,
    habitat: "pond",
    art: { bodyW: 0.78, bodyH: 0.78, belly: 0.84, bellyAt: 0.46, tail: "fan", tailH: 0.52, dorsal: 0.5,
      back: "#7fa6cf", bellyC: "#cfe2f4", fin: "#a8c6e2", pattern: "none", patternC: "#c9d6e6" },
  },
  {
    // PEARLBACK — a fish that grew a shell instead of scales: the back is armoured in overlapping NACRE
    // PLATES, each a thin film whose pastel colour is COMPUTED from its own thickness and viewing angle
    // (real interference, not a picked palette), so the sheen shifts across the body and no two plates match.
    // A ROW of graded PEARLS runs the dorsal ridge, and the topline BULGES over each one — the outline's own
    // ripple and the pearls come from one shared derivation, so the back reads both as pearls and as a lumpy
    // textured back rather than as beads laid on a smooth fish. The plating parts around every pearl. Deep,
    // round, heavy — a slow grazer carrying armour. Told from the Prismscale, the other faceted fish: that
    // one is SHATTERED cold crystal carrying almost no colour, this one is shingled nacre whose every plate
    // is a computed pastel interference hue. Broken stone against shell. See pearlback.tsx.
    id: "pearlback", name: "Pearlback", icon: "🦪", weight: 46, value: 102, difficulty: 0.64, sizeMul: 1.22,
    habitat: "pond",
    art: { bodyW: 0.72, bodyH: 0.8, belly: 0.78, bellyAt: 0.5, tail: "round", tailH: 0.46, dorsal: 0.4,
      back: "#c3c8d4", bellyC: "#f3eee6", fin: "#dcd2dd", pattern: "none", patternC: "#8f97a8", glow: "#ffe4f2" },
  },
  {
    // PRISMSCALE — a fish that looks like a stone that has been dropped. The flank is SHATTERED CRYSTAL:
    // irregular angular shards from a seeded Voronoi fracture, each with a dark seam and a catch-light on
    // the side facing the light, so what carries the fish is the fracture STRUCTURE. Colour is deliberately
    // a whisper — one mild gradient per shard from a narrow band of cold glass hues, with a few throwing
    // stronger fire. An earlier version put a full computed 400-700nm spectrum in each of 48 scales; at
    // sprite size that is not a mosaic of spectra, it is noise. The outline is a raked cut gem, 38.7 pct
    // dead straight, so the theme reaches the silhouette. See prismscale.tsx.
    id: "prismscale", name: "Prismscale", icon: "🌈", weight: 41, value: 120, difficulty: 0.7, sizeMul: 1.14, habitat: "pond",
    art: { bodyW: 0.66, bodyH: 0.72, belly: 0.82, bellyAt: 0.5, tail: "fan", tailH: 0.58, dorsal: 0.62, sail: true,
      back: "#cfd0e8", bellyC: "#f4f2ff", fin: "#e0d6f2", pattern: "none", glow: "#e8d6ff" },
  },
  {
    // DUSKMERE — the roster's only DUSK fish, and the animal is the weather that gates it: a flank that is a
    // real twilight column (three summed scattering terms, so the ember is a narrow band at the belly and the
    // violet takes the whole back), and fins with no outline anywhere — each is a fan of rays whose ink dies
    // along its own length, so every fin runs out into nothing at its trailing edge instead of ending on a
    // curve. Hard-edged body, edgeless fins; the contrast is the sprite. See duskmere.tsx.
    id: "duskmere", name: "Duskmere", icon: "🌇", weight: 38, value: 132, difficulty: 0.73, sizeMul: 1.2,
    habitat: "pond",
    art: { bodyW: 0.68, bodyH: 0.72, belly: 0.84, bellyAt: 0.5, tail: "fan", tailH: 0.58, dorsal: 0.54,
      back: "#181033", bellyC: "#ffac88", fin: "#743960", pattern: "none", glow: "#ff9a5c" },
  },
  {
    // MISTGILL — the roster's only FOG fish, and it is built out of the weather that gates it: a pale,
    // soft-edged body with no continuous outline anywhere, half dissolved into the water it hangs in, and
    // four gill plumes that trail off its head the way breath does on cold air. Colours are near-white over
    // a cool damp grey — the palest fish in the game — so it reads as condensation rather than as flesh.
    // Distinct from the three other half-there fish by mechanism: the Bog Wraith frays, the Aetherling
    // subtracts a starfield, the Willowisp emits light. This one DIFFUSES — see mistgill.tsx.
    id: "mistgill", name: "Mistgill", icon: "🌫️", weight: 35, value: 147, difficulty: 0.72, sizeMul: 1.1,
    habitat: "pond",
    art: { bodyW: 0.66, bodyH: 0.7, belly: 0.84, bellyAt: 0.5, tail: "long", tailH: 0.58, dorsal: 0.5,
      back: "#b9c6c8", bellyC: "#f2f7f7", fin: "#d6e2e3", pattern: "none", glow: "#e8f2f4" },
  },
  {
    id: "gildedidol", name: "Gilded Idol", icon: "🗿", weight: 28.5, value: 160, difficulty: 0.76, sizeMul: 1.24, habitat: "pond",
    art: { bodyW: 0.7, bodyH: 0.74, belly: 0.84, bellyAt: 0.5, tail: "fan", tailH: 0.56, dorsal: 0.64, sail: true,
      back: "#d0a02e", bellyC: "#ffe79a", fin: "#a67c22", pattern: "none", scales: 0.4, glow: "#ffe08a" },
  },
  {
    // XENOFIN — a swimming animal with a fish's parts assembled by something that had never seen a fish.
    // Body, fins and tail are all present and all wrong: the skull SPLITS into two divergent prongs that
    // never meet, with the mouth a recessed slot in the open water between them; there is one dorsal and
    // it is rooted too far forward; there is ONE pectoral rather than a pair; the underside carries three
    // identical keels in a row where a fish has a pelvic pair and an anal fin; the peduncle is not a
    // muscular taper but a JOINTED BOOM of three shrinking chitinous segments; and the tail is a single
    // stiff vane canted above the axis instead of a symmetric caudal. Four dark lenses in a vertical
    // column down the braincase do for eyes. Hence no `glow`: it emits nothing, and the alienness is
    // carried entirely by the SILHOUETTE rather than by colour or by a surface effect. The id has carried
    // a deep-sea anglerfish, then the Drownveil, then the Caulbearer; only the id, the stats and the gate
    // survive, because a save file stores the id of every fish a player has caught. Told from the pond's
    // pale half-there fish — Mistgill, Pearlback, Dewdrop, Bog Wraith, Inkfin, Aetherling — by anatomy
    // rather than by mechanism: every one of those is a horizontally-symmetric spindle, and this is a
    // deep-gutted asymmetric thing with a forked head and a segmented tail. See lanternjaw.tsx.
    id: "lanternjaw", name: "Xenofin", icon: "🥚", weight: 25.5, value: 172, difficulty: 0.78, sizeMul: 1.26,
    habitat: "pond",
    art: { bodyW: 0.76, bodyH: 0.8, belly: 0.86, bellyAt: 0.34, tail: "round", tailH: 0.42, dorsal: 0.4,
      back: "#b9c8c6", bellyC: "#8fa3a4", fin: "#a4b5b4", pattern: "none", bigEye: true },
  },
  {
    // INKFIN — the blackest fish in the game, and the only one you read by its EDGE. The flank is a near-black
    // #0d0f14 that carries no interior detail at any ship size, so the animal is given entirely by a RIM LIGHT
    // computed on the silhouette's own outward normal — about a third of the perimeter lit, the rest dark —
    // and by the INK it sheds. The ink is a real diffusion: each puff off the trailing fins spreads as
    // sqrt(t) while its concentration falls as 1/r², so the trail thins far faster than it grows. Told from
    // the Mistgill, the roster's other diffusing fish, by what diffuses: that one dissolves its own pale body,
    // this one is hard and opaque and diffuses what it emits. See inkfin.tsx.
    id: "inkfin", name: "Inkfin", icon: "🖤", weight: 21, value: 182, difficulty: 0.8, sizeMul: 1.28,
    habitat: "pond",
    art: { bodyW: 0.7, bodyH: 0.78, belly: 0.8, bellyAt: 0.46, tail: "fork", tailH: 0.54, dorsal: 0.56,
      back: "#0d0f14", bellyC: "#14171f", fin: "#101319", pattern: "none", glow: "#6f7fd8" },
  },
  {
    id: "bogwraith", name: "Bog Wraith", icon: "👻", weight: 19, value: 210, difficulty: 0.84, sizeMul: 1.32, habitat: "pond",
    art: { bodyW: 0.72, bodyH: 0.68, belly: 0.78, bellyAt: 0.5, tail: "long", tailH: 0.6,  dorsal: 0.5,
      back: "#1c2420", bellyC: "#2e3a34", fin: "#243028", pattern: "none", glow: "#7affc0" },
  },
  {
    id: "glimmerkoi", name: "Glimmerkoi", icon: "🌌", weight: 6.75, value: 320, difficulty: 0.9, sizeMul: 1.44, habitat: "pond",
    art: { bodyW: 0.74, bodyH: 0.72, belly: 0.84, bellyAt: 0.5, tail: "long", tailH: 0.62, dorsal: 0.52,
      back: "#1e2450", bellyC: "#3a4488", fin: "#2a3068", pattern: "none", glow: "#8ea6ff", bigEye: true },
  },

  // ── FANTASY STREAM (18) ──
  {
    id: "rapidsprite", name: "Rapidsprite", icon: "💠", weight: 93, value: 20, difficulty: 0.3, sizeMul: 0.5, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.3, belly: 0.3, bellyAt: 0.6, tail: "fork", tailH: 0.5, dorsal: 0.34,
      back: "#8fe0d0", bellyC: "#eafff8", fin: "#bfffe8", pattern: "none", glow: "#bfffe8" },
  },
  {
    id: "emberdace", name: "Emberdace", icon: "✨", weight: 87, value: 26, difficulty: 0.36, sizeMul: 0.62, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.3, belly: 0.32, bellyAt: 0.6, tail: "fork", tailH: 0.5, dorsal: 0.34,
      back: "#c8742e", bellyC: "#ffe0b0", fin: "#a65a22", pattern: "none", glow: "#ff9a4a" },
  },
  {
    // GRAVELBACK — a fish that has taken the riverbed onto its back: real pebbles set into the dorsal
    // surface, with the animal showing only where the stones stop. The armour is a SHADING MODEL, not a
    // palette — each stone has a lithology (albedo, gloss, specular sharpness) drawn from a weighted set of
    // five real rock types and its own surface normal tilted by where it sits across the back, so a
    // water-worn quartzite carries a small hard glint while the sandstone beside it carries a broad dull
    // sheen FROM THE SAME LIGHT. That differing response is what reads as "these are different rocks";
    // picked greys cannot do it. Distinct from the Pearlback, the roster's other armoured fish, by physics:
    // that one is thin-film interference on translucent nacre, this is opaque diffuse-plus-specular on
    // stone. The gaps between the stones are the point — they are the live flesh showing through, and
    // closing them turns a fish wearing gravel into a gravel-shaped object. See gravelback.tsx.
    id: "gravelback", name: "Gravelback", icon: "🪨", weight: 84, value: 31, difficulty: 0.4, sizeMul: 0.58,
    habitat: "stream",
    art: { bodyW: 0.9, bodyH: 0.44, belly: 0.6, bellyAt: 0.44, tail: "round", tailH: 0.44, dorsal: 0.34,
      back: "#4a4534", bellyC: "#b3a888", fin: "#5f5942", pattern: "none", patternC: "#8e9086" },
  },
  {
    id: "frostdart", name: "Frostdart", icon: "❄️", weight: 79, value: 36, difficulty: 0.44, sizeMul: 0.9, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.34, belly: 0.34, bellyAt: 0.58, tail: "fork", tailH: 0.52, dorsal: 0.38,
      back: "#7fb8e0", bellyC: "#eafaff", fin: "#bfe8ff", pattern: "none", glow: "#bfe8ff" },
  },
  {
    // EDDYWISP — a spinning eddy that has become an animal, and the exact fluid-dynamical opposite of the
    // Rapidsprite. That fish is a STANDING WAVE: irrotational, fixed in space, with the flow passing THROUGH
    // a periodic crest train on a straight axis. This one is a VORTEX — nothing but circulation. Its whole
    // character comes from one Rankine profile (solid-body rotation inside the core, 1/r free vortex outside
    // it). The curl is carried by the SURFACE and the MOTION rather than by the outline: three overlapping,
    // counter-rotating sets of spiral streamlines fill the whole flank and turn once per swim beat, so the
    // vortex is always moving while the fish holds its shape. The fins carry entrained grit, each fin's own
    // riding that fin. The bubbles trail aft as a wake. See eddywisp.tsx.
    id: "eddywisp", name: "Eddywisp", icon: "🌀", weight: 76, value: 44, difficulty: 0.47, sizeMul: 0.78,
    habitat: "stream",
    art: { bodyW: 0.8, bodyH: 0.5, belly: 0.5, bellyAt: 0.5, tail: "long", tailH: 0.5, dorsal: 0.3,
      back: "#5d8fa8", bellyC: "#cfe9f2", fin: "#8fc2d6", pattern: "none", glow: "#a8dcec" },
  },
  {
    id: "quartztrout", name: "Quartztrout", icon: "💎", weight: 72, value: 48, difficulty: 0.5, sizeMul: 1.0, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.36, belly: 0.36, bellyAt: 0.56, tail: "fork", tailH: 0.52, dorsal: 0.4,
      back: "#8a7ab0", bellyC: "#ece6f8", fin: "#b0a2d0", pattern: "none", speckle: "#5a4a8a", glow: "#d6c6ff" },
  },
  {
    id: "windribbon", name: "Windribbon", icon: "🎐", weight: 66, value: 58, difficulty: 0.54, sizeMul: 1.02, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.32, belly: 0.32, bellyAt: 0.58, tail: "long", tailH: 0.6, dorsal: 0.38,
      back: "#c8bce8", bellyC: "#f4f0ff", fin: "#ddd2f2", pattern: "none", glow: "#e8dcff" },
  },
  {
    // MOSSWHISKER — a fish so slow the river has grown on it. Every frond is a rooted CANTILEVER, not a
    // streamer: the deflection accumulates along the arc length, so the moss is nearly straight where it
    // leaves the fish and laid right over by the tip, which is what weed does and what a bristle does not.
    // The swim phase drives one shared drag field with a per-frond lag, so the bend runs head to tail rather
    // than the whole bed flexing in lockstep — and the drag never reverses, because water in a stream goes
    // one way. The moss roots along the BACK and the JAW only: a fish mossed all over is a rotting log, and
    // the bare scoured flanks are what say this one is still swimming. Under it the body is a dark matte
    // stone with no pattern and no sheen, broad and deep-bellied, because a streamlined fish wearing weed
    // would be a contradiction. Told from the Willowisp, the other green stream fish, by being its exact
    // inverse — that one is emitted light with no body at all. See mosswhisker.tsx.
    id: "mosswhisker", name: "Mosswhisker", icon: "🌿", weight: 64, value: 71, difficulty: 0.57, sizeMul: 0.94,
    habitat: "stream",
    art: { bodyW: 0.88, bodyH: 0.5, belly: 0.66, bellyAt: 0.46, tail: "round", tailH: 0.44, dorsal: 0.32,
      back: "#3f4740", bellyC: "#8f9384", fin: "#4c5449", pattern: "none", patternC: "#4d6b3a" },
  },
  {
    id: "willowisp", name: "Willowisp", icon: "🟢", weight: 55, value: 76, difficulty: 0.6, sizeMul: 1.0, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.32, belly: 0.34, bellyAt: 0.58, tail: "long", tailH: 0.6, dorsal: 0.38,
      back: "#2e6a5a", bellyC: "#7fd0b8", fin: "#3f8a72", pattern: "none", glow: "#7affd0" },
  },
  {
    // GLASSFIN — genuinely transparent, and the exact opposite optical claim to the Quartztrout. That fish
    // is a solid dielectric: its file traces a viewing ray at quartz's real n = 1.544, so light BENDS hard
    // going through it and the result is facets, caustics and doubled images. Clear animal tissue sits at
    // about n = 1.35 against water's 1.33 — very nearly index-matched — so light passes almost undeviated
    // and there is nothing to see. This file therefore has no ray tracing, no facets and no caustic at all,
    // and that absence is the point. What IS drawn is only what cannot be transparent: the vertebral column
    // with its ribs, the swim bladder (a GAS cavity, so the largest index step in the animal and the one
    // genuinely opaque organ), the fin rays because rays are bone, and the eye because a retina has to
    // absorb light to work. The body is not tinted but INTEGRATED — nested slices whose haze accumulates
    // with local thickness — and it carries no outline, because an edge is what a transparent animal does
    // not have. See glassfin.tsx.
    id: "glassfin", name: "Glassfin", icon: "🫧", weight: 52, value: 82, difficulty: 0.65, sizeMul: 1.06,
    habitat: "stream",
    art: { bodyW: 0.94, bodyH: 0.4, belly: 0.46, bellyAt: 0.44, tail: "fork", tailH: 0.54, dorsal: 0.28,
      back: "#9fc4d6", bellyC: "#e8f4fa", fin: "#cfe4ee", pattern: "none", patternC: "#cfe4ee" },
  },
  {
    id: "voltfin", name: "Voltfin", icon: "⚡", weight: 46, value: 96, difficulty: 0.7, sizeMul: 1.12, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.3, belly: 0.3, bellyAt: 0.6, tail: "long", tailH: 0.6, dorsal: 0.36,
      back: "#2a2a3a", bellyC: "#5a5a70", fin: "#3a3a4e", pattern: "none", glow: "#ffe14a" },
  },
  {
    // BELLWETHER — a bell that swims, and it rings BEFORE the weather turns, which is why it is gated on
    // `overcast` rather than on the storm itself: the Voltfin and the Stormeel ride the thunderstorm, and a
    // bellwether that only showed up during one would be warning of nothing. The body is a genuine hollow
    // SHELL — an outer wall and an inner wall with the cavity dark and open between them, thickened at the
    // lip the way a real bell is where it takes the strike — with the clapper hung from the crown on the
    // midline, swinging a quarter-beat BEHIND the body because a clapper is a pendulum on a moving support.
    // The fins are sheets of struck metal carrying nodal rings at the real zeros of J0, so the ripples crowd
    // toward the rim instead of being evenly spaced, and they breathe and decay with the swim phase. Told
    // from the Voltfin by what kind of physics it is: that one EMITS a branching discharge into the water,
    // this one RESONATES and nothing leaves it at all. See bellwether.tsx.
    id: "bellwether", name: "Bellwether", icon: "🔔", weight: 41, value: 108, difficulty: 0.74, sizeMul: 1.18,
    habitat: "stream",
    art: { bodyW: 0.86, bodyH: 0.56, belly: 0.7, bellyAt: 0.72, tail: "fan", tailH: 0.66, dorsal: 0.44,
      back: "#9a8f6e", bellyC: "#e6dfc6", fin: "#b8ad88", pattern: "none", patternC: "#f2ead0" },
  },
  {
    id: "mirrorpike", name: "Mirrorpike", icon: "🪞", weight: 35, value: 130, difficulty: 0.78, sizeMul: 1.5, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.34, belly: 0.34, bellyAt: 0.5, tail: "fork", tailH: 0.54, dorsal: 0.38,
      back: "#b8c4ce", bellyC: "#f2f6fa", fin: "#cdd6de", pattern: "none", glow: "#dfeaf2", bigEye: true },
  },
  {
    // FALLSPINE — a waterfall specialist, and every part of it solves one problem: staying put while the
    // water tries to take you downstream. The pectorals are enlarged into a gripping DISC a third of the
    // body long, held out flat and low rather than swept back, because this animal does not swim, it holds.
    // The back spines rake FORWARD, into the flow, and hook — a spine raking downstream levers itself out
    // under load where an upstream one digs in harder, which is why a fish hook, a crampon and a climbing
    // cam all point against the force; the sign of that rake is the whole mechanism. The white streaking is
    // entrained air in the shear layer, so it hugs the contour, crowds and thins where the flow accelerates
    // over the widest section, and broadens into the wake behind it — and it slides aft with the swim phase
    // so the water passes over a body holding still. Told from the Rapidsprite by which side of the problem
    // it is on: that fish IS the water, this one is the solid thing the water breaks over. See fallspine.tsx.
    id: "fallspine", name: "Fallspine", icon: "🪝", weight: 28.5, value: 160, difficulty: 0.82, sizeMul: 1.55,
    habitat: "stream",
    art: { bodyW: 0.94, bodyH: 0.44, belly: 0.62, bellyAt: 0.4, tail: "round", tailH: 0.4, dorsal: 0.3,
      back: "#3d4a52", bellyC: "#93a3ab", fin: "#4f5f68", pattern: "none", patternC: "#cfdde4" },
  },
  {
    id: "stormeel", name: "Stormeel", icon: "🌩️", weight: 23, value: 200, difficulty: 0.86, sizeMul: 1.6, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.26, belly: 0.26, bellyAt: 0.6, tail: "long", tailH: 0.58, dorsal: 0.34,
      back: "#5a6078", bellyC: "#9aa0b8", fin: "#4a5068", pattern: "none", patternC: "#4a5470", glow: "#a6b0d8" },
  },
  {
    // STARFIN — it carries the last of the night sky in its fins, and dawn is the SUBJECT rather than just
    // the gate. Stars do not fade together at dawn: a brightening sky raises the background until each star
    // is lost in it, so they vanish strictly faintest-first and the brightest survive longest. Every star
    // here therefore carries a real MAGNITUDE and is drawn only while its flux still beats the rising sky —
    // so the field has a hard limiting magnitude and the faint ones are GONE rather than grey, which is what
    // separates a dawn from a dimmer. The wash is weaker toward the fin margins, so "brightest at the
    // margins, fading into a paling body" falls out of that one rule instead of being two tuned
    // brightnesses. Only the marginal stars twinkle, because a bright star sits above the noise. Told from
    // the Aetherling, the roster's other starfield fish, by an opposite claim: that one has no flesh and its
    // stars are BEHIND it, seen through a void body; this is an emphatically material animal with points of
    // light held IN the fin membrane. See starfin.tsx.
    id: "starfin", name: "Starfin", icon: "🌠", weight: 19, value: 238, difficulty: 0.89, sizeMul: 1.62,
    habitat: "stream",
    art: { bodyW: 0.94, bodyH: 0.42, belly: 0.5, bellyAt: 0.44, tail: "fork", tailH: 0.68, dorsal: 0.6,
      back: "#2b3557", bellyC: "#c8cfe4", fin: "#1b2340", pattern: "none", patternC: "#dce8ff" },
  },
  {
    // CHRONOFIN — a clockwork fish, and the rarest of the new stream fantasy catches. The whole sprite comes
    // out of one mechanical fact: AN ESCAPEMENT QUANTISES TIME. It takes a continuous drive and lets it out
    // in discrete equal steps, and that same quantisation drives the fins — so this is the only fish on the
    // roster whose fins SNAP to a position and hold rather than sweeping on a sine, which needs a customAnim
    // because StandardSwim is continuous by construction. The gear train has DERIVED ratios (each wheel at
    // ±drive·z0/z, sign alternating, because meshing wheels counter-rotate at rates inverse to their teeth),
    // so the small wheels race while the great wheel creeps; picking a speed per wheel is what makes drawn
    // clockwork read as spinning stickers. The movement is seen through a bezelled APERTURE cut in solid
    // brass plate — not a transparent body, which is the Glassfin's claim.
    // NOTE: weight 10.5 is the second rung of the stream LEGENDARY band (see the weight ladder above).
    // The rung is ORDERING ONLY — it shares the band's single 2% base with the Salmon and the Huchen, so
    // sliding it inside the band changes no odds. Keep it under the Very Rare seam at 19; that is the
    // only move a player would feel. Its scarcity is bought by its conditions, not by this number.
    id: "chronofin", name: "Chronofin", icon: "⚙️", weight: 10.5, value: 285, difficulty: 0.91, sizeMul: 1.36,
    habitat: "stream",
    art: { bodyW: 0.9, bodyH: 0.46, belly: 0.54, bellyAt: 0.48, tail: "fork", tailH: 0.58, dorsal: 0.46,
      back: "#b08a3c", bellyC: "#e8d9a8", fin: "#8d6f30", pattern: "none", patternC: "#c4453f" },
  },
  {
    id: "aetherling", name: "Aetherling", icon: "🔮", weight: 4.25, value: 340, difficulty: 0.92, sizeMul: 1.4, habitat: "stream",
    art: { bodyW: 1.0, bodyH: 0.34, belly: 0.34, bellyAt: 0.54, tail: "fork", tailH: 0.54, dorsal: 0.38,
      back: "#241a3a", bellyC: "#463a68", fin: "#342a52", pattern: "none", glow: "#b48aff", bigEye: true },
  },
];

const FISH_BY_ID = new Map(FISH.map((f) => [f.id, f]));
export function fishDef(id: string): FishDef | undefined { return FISH_BY_ID.get(id); }

/**
 * Bite wait window (ms): a fish takes somewhere in this range to bite after the cast. The cast site divides
 * the roll by the loadout's `biteRate`, so this window is the WORST case (a day-one rig) and gear buys the
 * rest. Targets: ~8 s average on base stats, ~3 s average on the best rig a player can actually assemble.
 *
 * THE ARITHMETIC (do not re-tune these by feel — re-run it):
 *
 *  The divisor is `deriveLoadout`'s `biteRate`, which is NOT one stat. It is the biteRate curve TIMES luck's
 *  35% share:  biteRate = statEffect("biteRate", brPts) × (1 + (statEffect("luck", luPts) − 1) × 0.35),
 *  with statEffect = 1 + gain·(1 − e^(−p/scale)); biteRate {gain 0.9, scale 150}, luck {gain 1.2, scale 175}.
 *
 *  BASE — level 1, the free starter rig `effectiveEquipped` hands every new player (hook1 → biteRate +5, four
 *  pieces → luck +4), no rolls. That is 15 biteRate points and 9 luck points, NOT zero: a bare ×1.00 loadout
 *  is unreachable in game, so solving against it would have missed low.
 *      biteRate = 1.0876 × 1.0192 = ×1.109
 *
 *  CEILING — L50 (biteRate 83.5, luck 68.7 from levels) wearing five legendaries, 25 rolled lines all at the
 *  top of `rollRange(50, "legendary")` = 57 points each. Every slot pool offers both biteRate and luck, so a
 *  player chasing bite speed puts all 25 lines on the two. The best SPLIT is ~12 biteRate / 13 luck — both
 *  curves saturate, so hoarding one stat is worse than feeding both:
 *      25/0 → ×2.159   20/5 → ×2.592   12/13 → ×2.683   0/25 → ×1.965
 *  So the real ceiling is ×2.683, against an unreachable asymptote of 1.9 × 1.42 = ×2.698.
 *
 *  THE RANGE ONLY HAS ONE FREE PARAMETER — its mean — and the multiplier spread is fixed at 2.683/1.109 =
 *  2.42×, while 8 s → 3 s asks for 2.67×. The two targets cannot both be hit. The residual is split evenly:
 *  mean 8700 ms gives 7.85 s at base and 3.24 s at the ceiling, each within 0.25 s. Mid-game (L25, ~100 gear
 *  points on both stats) lands ×1.91 → 4.6 s, a smooth ride between them.
 *
 *  MIN 4200 is the number to be careful with. At the ceiling it is a 1.57 s floor, which keeps a kitted rig
 *  clearly faster than a bare one while staying above the 0.5–1.0 s GUARANTEED bite a fish-shadow cast gets
 *  (useGarden's `castAtShadow`) — shadows must stay the fastest bite in the game or the intuition stat and
 *  the whole shadow feature stop paying for themselves. MAX 13200 is the worst base-stat wait, ~11.9 s.
 *
 *  `fishSpawn.CAST_BITE_AVG_S` mirrors this midpoint for the "expected time to catch" estimate; move these
 *  and move that too.
 */
export const BITE_MIN_MS = 4200;
export const BITE_MAX_MS = 13200;
/** After the bite, how long the player has to react (click to start reeling) before the fish escapes. */
export const REACT_WINDOW_MS = 1400;

/** Pick a random bite delay in [BITE_MIN_MS, BITE_MAX_MS] given a 0..1 roll. */
export function biteDelay(roll: number): number {
  return Math.round(BITE_MIN_MS + roll * (BITE_MAX_MS - BITE_MIN_MS));
}

/**
 * NOT THE GAME'S FISH PICK. This is a weight-proportional draw, and the shipped spawn path does not use
 * it: real casts go through `pickFishByChance` (sim/fishSpawn.ts), which rolls DIRECT per-fish percentages
 * derived from tier + conditions + drivers, and can return no bite at all. This one is deterministic on
 * its roll and always returns a fish, which is exactly why the DEV SCREENSHOT HARNESS keeps it — the
 * `?shot=garden&fish=…` path in useGarden.ts is its only caller, so a captured frame shows the same fish
 * every run. Do not wire it back into a cast; weight has not driven encounter rates since the direct-%
 * model landed.
 *
 * When `water` ("pond" | "stream") is given, only fish whose habitat matches that water are eligible (plus
 * any "both" generalists, if the roster gains them); omitted → the full roster. If a filter somehow empties
 * the pool, fall back to the full roster so a cast never fails. The roster splits 35 pond / 35 stream (no
 * "both"), so a filtered draw always has its 35.
 */
export function pickFish(roll: number, water?: "pond" | "stream"): FishDef {
  const pool = water ? FISH.filter((f) => f.habitat === water || f.habitat === "both") : FISH;
  const list = pool.length ? pool : FISH;
  const total = list.reduce((s, f) => s + f.weight, 0);
  let x = roll * total;
  for (const f of list) { if ((x -= f.weight) <= 0) return f; }
  return list[0]!;
}

/**
 * Tuning for the IN-WORLD "fight the fish" minigame (all derived from a fish's difficulty 0..1). Rates are
 * per-second; health/escape/tension are 0..1.
 *
 * The fight: the fish holds a horizontal RUN direction for a random interval then flips (harder fish flip
 * more often + run harder). A directional arrow points OPPOSITE the run (the side to pull toward). Pulling
 * the rod (cursor) against the run drains the fish's HEALTH — faster the harder you pull, but the harder
 * you pull the higher the line TENSION (white→yellow→orange→red); holding max tension too long SNAPS the
 * line (loss). Slacking (pulling toward the fish) relaxes tension. If you stop fighting, health slowly
 * REGENS; once it's back to full, a fast blue ESCAPE meter fills — if it fills, the fish shakes off (loss).
 * Catch = health reaches 0.
 */
export interface FightParams {
  /** min / max seconds the fish holds a run direction before flipping (harder → shorter, flips more). */
  flipMin: number;
  flipMax: number;
  /** how hard the fish RUNS — drives the bobber's roam speed + splash intensity (0..1-ish). */
  runStrength: number;
  /** health drained per second at a REFERENCE pull (pull=1). Actual drain scales with how far you pull. */
  drain: number;
  /** health REGENERATED per second when you're not effectively fighting (slack / wrong side). */
  regen: number;
  /** how fast the blue ESCAPE meter fills (per second) once health has regenerated back to full. FAST —
   *  passivity is punished. */
  escapeSpeed: number;
  /** health the fight STARTS at (1 = full; a small cushion could lower it, but a fresh fish is full). */
  start: number;
  /** the STRAIN at which the line snaps (gear can raise it via lineStrength). Absent ⇒ FishFight.SNAP_STRAIN. */
  snapStrain?: number;
}

/** LINE STRENGTH's escape-gauge relief: the fill speed drops by `LINE_ESCAPE_SLOPE` per point of line
 *  multiplier above 1, never below `LINE_ESCAPE_FLOOR` of the base (so at most −25%). Exported because the
 *  Tackle readout (ui/FishingGearModal.tsx) quotes the same numbers. */
export const LINE_ESCAPE_SLOPE = 0.18;
export const LINE_ESCAPE_FLOOR = 0.75;

/**
 * Apply the player's fishing GEAR loadout to a fish's fight params: a better REEL drains the fish faster, a
 * stronger LINE raises the snap point. Pure — returns a new params object. (biteRate/hookWindow are applied to
 * the bite wait + react window at the cast site, not here; luck/lure affect the odds roll.)
 */
export function applyLoadoutToFight(
  params: FightParams, loadout: { drainPower: number; lineStrength: number },
): FightParams {
  const ls = Math.max(1, loadout.lineStrength);
  return {
    ...params,
    drain: params.drain * Math.max(0.5, loadout.drainPower),         // POWER → wear the fish down faster
    snapStrain: 1.15 * ls,                                           // LINE STRENGTH → tolerates more stretch
    // LINE STRENGTH also slightly reduces the escape-gauge fill (so a beefier line is more forgiving when you're
    // on the correct side but not pulling tight enough). Up to ~-25% at max line strength.
    escapeSpeed: params.escapeSpeed * Math.max(LINE_ESCAPE_FLOOR, 1 - (ls - 1) * LINE_ESCAPE_SLOPE),
  };
}

/**
 * NEW-ANGLER GRACE, never shown to the player: how many times longer the escape meter takes to fill. A fresh fish is
 * at full health, so the meter runs from the moment of the hook-set, and a new player is still working out what they
 * are looking at when it fills (1.1–1.8 s at full speed). The tutorial's fish gets the most; after that it eases off
 * in even steps with each fish landed, and is gone from the tenth catch on. Lost fish do not count, so a player who is
 * struggling keeps the help.
 */
export const ESCAPE_GRACE_TUTORIAL = 15;
export const ESCAPE_GRACE_FIRST_CATCH = 8;
export const ESCAPE_GRACE_CATCHES = 10;
export function escapeGrace(catches: number, tutorial: boolean): number {
  if (tutorial) return ESCAPE_GRACE_TUTORIAL;
  const left = Math.max(0, ESCAPE_GRACE_CATCHES - Math.max(0, catches)) / ESCAPE_GRACE_CATCHES;
  return 1 + (ESCAPE_GRACE_FIRST_CATCH - 1) * left;
}
export function withEscapeGrace(params: FightParams, grace: number): FightParams {
  return { ...params, escapeSpeed: params.escapeSpeed / Math.max(1, grace) };
}

export function fightParams(difficulty: number): FightParams {
  const d = Math.max(0, Math.min(1, difficulty));
  return {
    flipMin: 1.6 - d * 0.9,          // easy holds ~1.6–3.0s → hard flips often (~0.7–1.4s)
    flipMax: 3.0 - d * 1.6,
    runStrength: 0.5 + d * 0.5,      // harder fish run harder (bigger roam + splash)
    // Brisk enough to feel responsive: at ~full correct pull, easy ≈ 3s to land, hard ≈ 6–7s.
    drain: 0.34 - d * 0.12,          // easy ≈ 0.34/s at full pull → hard ≈ 0.22/s
    regen: 0.07 + d * 0.05,          // easy regens ~0.07/s → hard recovers faster (~0.12/s) if you slack
    escapeSpeed: 0.55 + d * 0.35,    // blue escape fills FAST once health is full (≈1.1–2.2s to escape)
    start: 1.0,
  };
}

export interface FishCatch { id: string; name: string; value: number; at: number; }

/** Defensive coerce of the persisted per-fish caught tally (fishId → count). CUMULATIVE — this is the
 *  lifetime catch count and nothing subtracts from it; selling writes `fishSold` instead. */
export function coerceFishCaught(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (FISH_BY_ID.has(k) && typeof v === "number" && v > 0) out[k] = Math.floor(v);
  }
  return out;
}

/** Defensive coerce of the persisted per-fish SOLD tally (fishId → count). Same shape and rules as
 *  `coerceFishCaught`; kept separate so the two never get wired to the same validator by accident. */
export function coerceFishSold(raw: unknown): Record<string, number> {
  return coerceFishCaught(raw);
}

/** The SELLABLE bag: what the player has caught minus what they have already sold, per species, with the
 *  emptied species dropped. Selling must never touch `fishCaught` — that map is the lifetime tally the
 *  `fishCaught:<id>` achievement ladders, `fishSpecies` and the Field Guide's "found" flag all read, and a
 *  player who sells as they go would otherwise un-discover their own fish. */
export function fishStockOf(caught: Record<string, number>, sold: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, n] of Object.entries(caught)) {
    const left = n - (sold[id] ?? 0);
    if (left > 0) out[id] = left;
  }
  return out;
}

/** Defensive coerce of the persisted set of fish CONDITIONS the player has revealed (via message-in-a-bottle
 *  pickups). Entries are `"<fishId>:<condKey>"`; a bare id from an older save is grandfathered to "all
 *  conditions revealed" rather than re-locked — see sim/clueReveal.ts. */
export function coerceRevealedFish(raw: unknown): string[] {
  return coerceReveals(raw, (id) => FISH_BY_ID.has(id));
}
